import type { Pinia } from "pinia";
import { storeToRefs } from "pinia";
import type { NavigationGuardNext, RouteLocationNormalized, RouteLocationNormalizedLoaded } from "vue-router";

import * as tmi from "@/constants";
import routeManager from "@/router/route-manager";
import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";

/**
 * The single auth gate — registered as `router.beforeResolve( createAuthGuard( pinia ) )`.
 * Extracted from router/index.ts (tmi-6gh, LD6) so specs can drive it directly with fake
 * `to` / `next` and real store modules instead of standing up a router.
 *
 * Route contract: `meta.public` skips auth entirely; otherwise the route is authenticated and
 * `meta.permission` (feature key) plus optional `meta.shortCode` (product tier) must both pass.
 * NOTE (R48): `hasFeatureAccess( '' )` is always false, so an authenticated route that omits
 * `meta.permission` 403s for everyone — every non-public route must declare one.
 */
export function createAuthGuard( pinia: Pinia ) {
    return async ( to: RouteLocationNormalized, _: RouteLocationNormalizedLoaded, next: NavigationGuardNext ): Promise<void> => {
        const { isAuthenticated } = storeToRefs( useAuthStore( pinia ) );
        if ( !isAuthenticated.value ) {
            sessionStorage.removeItem( 'nextLinkAfterLogin' );
        }

        const routeRequiresAuthentication: boolean = !to.meta?.public;
        const routeRequiresSpecificPermissions: boolean = !!( to.meta?.permission );

        // The selected route requires authorization
        if ( routeRequiresAuthentication || routeRequiresSpecificPermissions ) {
            // The following series of predicates will fire in the situation where there exists a valid authentication token
            // in localstorage, but the permissions have not been loaded/re-loaded into memory. This can happen in multiple scenarios:
            //      1) The user manually edits the url in the browser
            //      2) The user refreshes the browser
            //      3) A new tab/window is opened during the time window when the authentication token is still valid
            //      4) The cache was purged (or never filled) while the token survived — e.g. a PWA update reload mid-login

            // Divert the user to the sign-in page b/c they are requesting a route that requires authentication and they are not authenticated
            if ( !isAuthenticated.value ) {
                sessionStorage.setItem( 'nextLinkAfterLogin', to?.path ?? routeManager.homePath() );
                next( { name: tmi.SignInName } );
                return;
            }

            const permissionsStore = usePermissionsStore( pinia );
            if ( !permissionsStore.isInitialized ) {
                // Self-heal (LD2): localStorage first, then profile + grants from the server.
                // Never rejects; a failure falls through to the 403 path below.
                await permissionsStore.ensureInitializedAsync();
                // Attempt to pre-initialize all auth-required stores,
                // but only in the case where permissions had to be loaded/reloaded
                const { initializeAsync } = useApplicationStore( pinia );
                await initializeAsync();
            }

            // User is authenticated, validate that they have the correct permissions
            const metaPermission: string = `${ to.meta?.permission ?? '' }`;
            let isAuthorizedForRoute: boolean = permissionsStore.hasFeatureAccess( metaPermission );

            // Some routes are additionally gated by a product short code (e.g. 'SSS'
            // for the DMI alerts feed). meta.permission is single-valued and
            // feature-based, so the short code rides in its own optional field.
            // Exclusive tiers (like SSS) are filtered server-side out of the
            // alliance/fullaccess umbrella expansion, so a positive short-code
            // match is proof of a direct grant — no exclusion list is needed here.
            const metaShortCode: string = `${ to.meta?.shortCode ?? '' }`;
            if ( isAuthorizedForRoute && metaShortCode ) {
                isAuthorizedForRoute = permissionsStore.hasAccessBasedOnShortCode( metaShortCode );
            }

            // user is NOT authorized for route
            if ( !isAuthorizedForRoute ) {
                // Marking the current status for the user as "Forbidden" or 403
                next( { path: routeManager.forbiddenPath() } );
                return;
            }
        }

        // The selected route is not protected
        next();
    };
}
